import Vue from 'vue'
import Resource from '@/plugins/sdk/Resource'
import { moment } from '@/plugins/moment'
import isPlainObject from 'lodash/isPlainObject'
import cloneDeep from 'lodash/cloneDeep'

export * from '@/plugins/sdk/operators'

/**
 * Parse the given value.
 *
 * @param      {mixed}  value   The value to parse
 * @return     {mixed}
 */
function parseValue (value) {
  let response

  if (Array.isArray(value)) {
    response = value.map(parseValue)
  } else if (value instanceof Resource) {
    response = value.id
  } else if (isPlainObject(value)) {
    response = Object.keys(value).map(p => parseValue(value[p]))
  } else if (value instanceof Date || value instanceof moment) {
    response = moment(value).format('YYYY-MM-DD HH:mm:ss')
  } else if (typeof value !== 'undefined') {
    response = `${value}`
  }

  return response
}

let uid = 0

export default class {
  /**
   * Constructs the Filter object.
   *
   * @return     {Object}
   */
  constructor (field, operator, value, label) {
    Object.defineProperties(this, {
      id: {
        configurable: false,
        enumerable: true,
        value: uid += 1,
        writable: false
      },
      field: {
        configurable: true,
        enumerable: false,
        value: field,
        writable: true
      },
      operator: {
        configurable: true,
        enumerable: false,
        value: operator,
        writable: true
      },
      value: {
        configurable: true,
        enumerable: false,
        value,
        writable: true
      },
      label: {
        configurable: false,
        enumerable: false,
        value: label || false,
        writable: true
      }
    })

    Vue.util.defineReactive(this, 'field')
    Vue.util.defineReactive(this, 'operator')
    Vue.util.defineReactive(this, 'value')
    Vue.util.defineReactive(this, 'label')

    return Object.seal(this)
  }

  get isValid () {
    return (
      typeof this.field !== 'undefined' &&
      typeof this.operator !== 'undefined' &&
      this.operator.isValidValue(this.value)
    )
  }

  /**
   * @see        {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON()_behavior}
   * @return     {String}
   */
  toJSON () {
    return { field: this.field, operator: this.operator, value: this.value }
  }

  /**
   * @see        {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString}
   * @return     {String}
   */
  toString () {
    let value = cloneDeep(parseValue(this.value))

    if (Array.isArray(value)) {
      if (this.operator.operator === 'is between' && value.length === 2) {
        // Handle 'is between' specifically for arrays with two elements
        return `${this.field} ${this.operator.operator} ${value[0]}${this.operator.separator}${value[1]}`
      }
      return value
        .map(v => `${new this.constructor(this.field, this.operator, v)}`)
        .join(this.operator.separator || ' and ')
    } else {
      return [ this.field, this.operator.operator, value ].filter(Boolean).join(' ')
    }
  }
}
